/* @flow */
import produce from 'immer';
import { message } from 'antd';
import { actionTypes } from './actions';
import { formatNumberToLocale } from '@/utils/common';
import { defineServerErrors } from '@/utils/redux';

const {
  FETCH_LEADS,
  FETCH_SUCCESS,
  FETCH_FAILED,

  FETCH_LEADS_SUMMARY,
  FETCH_SUMMARY_SUCCESS,
  FETCH_SUMMARY_FAILED,

  CHANGE_TAB,

  TABLE_CHANGE,
} = actionTypes;

type State = {
  isFetching: boolean,
  isSummaryFetching: boolean,
  data: LeadT[],
  summaryData: LeadSummaryT,
  errors: ?Array<string>,
  activeTab: string,
  tableOptions: TableT,
};

const initialState = {
  isFetching: false,
  isSummaryFetching: false,
  summaryData: {
    totalRooftopSolarCapacity: 0,
    totalSystemSize: 0,
    totalSolarCost: 0,
    totalLeads: 0,
    totalCapacity: '0.00',
    unit: 'kW',
  },
  data: [],
  errors: null,
  activeTab: 'leads-table',
  tableOptions: {
    filters: {},
    sorter: {
      order: '',
      field: '',
    },
    search: '',
    page: 1,
    count: 0,
    limit: 50,
  },
};

export default (state: State = initialState, action: ActionT = {}): State =>
  produce(state, (draft) => {
    // immer returns initial state by default
    // eslint-disable-next-line default-case
    switch (action.type) {
      case FETCH_LEADS:
        draft.isFetching = true;
        return;
      case FETCH_SUCCESS: {
        const { results, page, count, limit } = action.payload;
        draft.isFetching = false;
        draft.data = results;
        draft.tableOptions = {
          ...draft.tableOptions,
          page,
          limit,
          count,
        };
        draft.errors = initialState.errors;
        return;
      }
      case FETCH_FAILED: {
        draft.isFetching = false;
        draft.errors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.errors.forEach((error) => message.error(error, 10));
        return;
      }
      case FETCH_LEADS_SUMMARY:
        draft.isSummaryFetching = true;
        return;
      case FETCH_SUMMARY_SUCCESS: {
        let totalCapacity = action.payload.total_system_size;
        if (action.payload.total_system_size > 10000) {
          totalCapacity = action.payload.total_system_size / 1000;
        }
        if (isNaN(totalCapacity)) {
          if (action.payload.total_rooftop_solar_capacity > 10000) {
            totalCapacity = action.payload.total_rooftop_solar_capacity / 1000;
          }
          totalCapacity = action.payload.total_rooftop_solar_capacity;
        }
        draft.summaryData = {
          totalSolarCost: action.payload.total_solar_cost,
          totalCapacity: formatNumberToLocale({
            number: totalCapacity,
            style: 'decimal',
            maximumFractionDigits: totalCapacity > 10000 ? 2 : 0,
            minimumFractionDigits: totalCapacity > 10000 ? 2 : 0,
          }),
          totalRooftopSolarCapacity: action.payload.total_rooftop_solar_capacity,
          totalSystemSize: action.payload.total_system_size,
          totalLeads: action.payload.total_leads,
          unit: totalCapacity > 10000 ? 'MW' : 'kW',
        };
        draft.isSummaryFetching = false;
        return;
      }
      case FETCH_SUMMARY_FAILED: {
        draft.summaryData = state.summaryData;
        draft.errors = defineServerErrors(action.payload, 'Some error has occurred. Please try again later.');
        draft.errors.forEach((error) => message.error(error, 10));
        draft.isSummaryFetching = false;
        return;
      }
      case CHANGE_TAB: {
        draft.activeTab = action.payload.tabName;
        draft.tableOptions = {
          ...draft.tableOptions,
          ...action.payload.tableOptions,
        };
        return;
      }
      case TABLE_CHANGE:
        draft.tableOptions = {
          ...draft.tableOptions,
          ...action.payload,
        };
        return;
    }
  });
